// TenantContext.tsx - Contexto Multi-Tenant para Sistema Courier SaaS
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { applyPwaBranding } from '../utils/pwa';

// Tipos
export interface TenantBranding {
    logo_url: string | null;
    logo_white_url: string | null;
    favicon_url: string | null;
    primary_color: string;
    secondary_color: string;
    accent_color: string;
    company_name: string;
    tagline: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    address: string | null;
    website_url: string | null;
}

export interface TenantPlan {
    id: string;
    name: string;
    limits: {
        users: number;
        packages_per_month: number;
        agents: number;
        couriers: number;
        storage_gb: number;
    };
    features: string[];
}

export interface Tenant {
    id: string;
    name: string;
    subdomain: string;
    status: 'trial' | 'active' | 'suspended' | 'cancelled';
    plan: TenantPlan | null;
    branding: TenantBranding;
    trial_ends_at: string | null;
    has_custom_domain: boolean;
    custom_domain: string | null;
}

interface TenantContextType {
    tenant: Tenant | null;
    loading: boolean;
    error: string | null;
    isWhiteLabel: boolean;
    refreshTenant: () => Promise<void>;
}

// Tenant de ITOBOX (el primer cliente real del SaaS).
// Se usa cuando el subdominio es 'itobox' o 'itobox.sistemacourier.com'.
const ITOBOX_TENANT: Tenant = {
    id: 'itobox-default',
    name: 'ITOBOX Courier',
    subdomain: 'itobox',
    status: 'active',
    plan: {
        id: 'enterprise',
        name: 'Enterprise',
        limits: {
            users: 9999,
            packages_per_month: 999999,
            agents: 9999,
            couriers: 9999,
            storage_gb: 1000,
        },
        features: ['*'],
    },
    branding: {
        logo_url: '/icons/itobox/logo.png',
        logo_white_url: null,
        favicon_url: null,
        primary_color: '#FF3B3F',
        secondary_color: '#1F2937',
        accent_color: '#3B82F6',
        company_name: 'ITOBOX Courier',
        tagline: 'Sistema de gestión para empresas de courier y mensajería',
        contact_email: 'info@itobox.com',
        contact_phone: null,
        address: null,
        website_url: 'https://itobox.com',
    },
    trial_ends_at: null,
    has_custom_domain: false,
    custom_domain: null,
};

// Tenant genérico para subdominios desconocidos (prueba, demo, clientes nuevos, etc.)
// Muestra la plataforma con branding neutro hasta que el tenant esté registrado en la BD.
const GENERIC_TENANT: Tenant = {
    id: 'generic-default',
    name: 'Sistema Courier',
    subdomain: '',
    status: 'active',
    plan: {
        id: 'enterprise',
        name: 'Enterprise',
        limits: {
            users: 9999,
            packages_per_month: 999999,
            agents: 9999,
            couriers: 9999,
            storage_gb: 1000,
        },
        features: ['*'],
    },
    branding: {
        logo_url: null,
        logo_white_url: null,
        favicon_url: null,
        primary_color: '#3B82F6',
        secondary_color: '#1F2937',
        accent_color: '#3B82F6',
        company_name: 'Sistema Courier',
        tagline: 'Sistema de gestión para empresas de courier y mensajería',
        contact_email: null,
        contact_phone: null,
        address: null,
        website_url: null,
    },
    trial_ends_at: null,
    has_custom_domain: false,
    custom_domain: null,
};

const TenantContext = createContext<TenantContextType | undefined>(undefined);

// Hook para usar el contexto
export const useTenant = () => {
    const context = useContext(TenantContext);
    if (context === undefined) {
        throw new Error('useTenant must be used within a TenantProvider');
    }
    return context;
};

// Leer override de personalización guardado por el admin del tenant
const BRANDING_STORAGE_KEY = 'tenant_branding_override';
const getBrandingOverride = (): Partial<TenantBranding> => {
    try {
        const stored = localStorage.getItem(BRANDING_STORAGE_KEY);
        return stored ? JSON.parse(stored) : {};
    } catch {
        return {};
    }
};

// Aplicar branding dinámico
const applyBranding = (branding: TenantBranding) => {
    const root = document.documentElement;

    // Aplicar colores como variables CSS
    root.style.setProperty('--primary-color', branding.primary_color);
    root.style.setProperty('--secondary-color', branding.secondary_color);
    root.style.setProperty('--accent-color', branding.accent_color);

    // Actualizar favicon si existe
    if (branding.favicon_url) {
        const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
        if (favicon) {
            favicon.href = branding.favicon_url;
        }
    }

    // Actualizar título de la página
    if (branding.company_name) {
        document.title = branding.company_name;
    }

    // Nombre, color e ícono al instalar en el celular (solo en el dominio de una empresa)
    const host = window.location.hostname;
    if (host !== 'sistemacourier.com' && host !== 'www.sistemacourier.com') {
        const { type, value } = getTenantFromHostname();
        applyPwaBranding({
            subdomain: type === 'subdomain' ? value : '',
            companyName: branding.company_name,
            themeColor: branding.primary_color,
        });
    }
};

// Detectar tenant desde el hostname
const getTenantFromHostname = (): { type: 'subdomain' | 'custom', value: string } => {
    const hostname = window.location.hostname;

    // Si es localhost, usar subdomain del query param o 'demo'
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        const params = new URLSearchParams(window.location.search);
        return { type: 'subdomain', value: params.get('tenant') || 'demo' };
    }

    // Si termina en nuestro dominio SaaS, es subdomain
    const saasDomainsPattern = /^([^.]+)\.(sistemacourier|itobox)\.(com|local)$/;
    const subdomainMatch = hostname.match(saasDomainsPattern);

    if (subdomainMatch) {
        return { type: 'subdomain', value: subdomainMatch[1] };
    }

    // Si no, es un dominio personalizado (White Label)
    return { type: 'custom', value: hostname };
};

interface TenantProviderProps {
    children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
    const [tenant, setTenant] = useState<Tenant | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchTenant = async () => {
        try {
            setLoading(true);
            setError(null);

            // Dominio principal (landing, registro, páginas legales): no es una empresa.
            // Antes se buscaba la empresa "www" y el servidor respondía 404 en cada visita.
            const host = window.location.hostname;
            if (host === 'sistemacourier.com' || host === 'www.sistemacourier.com') {
                setTenant(GENERIC_TENANT);
                applyBranding(GENERIC_TENANT.branding);
                return;
            }

            const { type, value } = getTenantFromHostname();
            const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3001';

            // Construir URL del endpoint según si es subdominio o dominio custom (White Label)
            const url = type === 'subdomain'
                ? `${apiBase}/api/tenants/by-subdomain/${value}`
                : `${apiBase}/api/tenants/by-domain/${encodeURIComponent(value)}`;

            // Llamar al backend con timeout de 8 segundos
            // (Render en plan gratuito puede tardar hasta 60s en despertar;
            //  si no responde a tiempo, usamos el fallback local)
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 8000);

            let response: Response;
            try {
                response = await fetch(url, { signal: controller.signal });
            } finally {
                clearTimeout(timeoutId);
            }

            if (response.ok) {
                // Backend respondió: construir Tenant con los datos reales
                const data = await response.json();
                const tenantFromApi: Tenant = {
                    id: data.id,
                    name: data.name,
                    subdomain: data.subdomain,
                    status: data.status,
                    plan: data.plan || null,
                    branding: {
                        // ITOBOX trae su logo en public/icons/itobox si no subió uno propio
                        logo_url: data.branding?.logo_url || (data.subdomain === 'itobox' ? '/icons/itobox/logo.png' : null),
                        logo_white_url: data.branding?.logo_white_url || null,
                        favicon_url: data.branding?.favicon_url || null,
                        primary_color: data.branding?.primary_color || '#3B82F6',
                        secondary_color: data.branding?.secondary_color || '#1F2937',
                        accent_color: data.branding?.accent_color || '#3B82F6',
                        company_name: data.branding?.company_name || data.name,
                        tagline: data.branding?.tagline || null,
                        contact_email: data.branding?.contact_email || null,
                        contact_phone: data.branding?.contact_phone || null,
                        address: data.branding?.address || null,
                        website_url: data.branding?.website_url || null,
                    },
                    trial_ends_at: data.trial_ends_at || null,
                    has_custom_domain: data.has_custom_domain || false,
                    custom_domain: data.custom_domain || null,
                };

                const tenantWithOverride = {
                    ...tenantFromApi,
                    branding: { ...tenantFromApi.branding, ...getBrandingOverride() }
                };
                setTenant(tenantWithOverride);
                applyBranding(tenantWithOverride.branding);

            } else if (response.status === 404) {
                // Tenant no existe en BD — mostrar branding genérico
                console.warn(`[TenantContext] Tenant "${value}" no encontrado en BD, usando branding genérico`);
                setTenant(GENERIC_TENANT);
                applyBranding(GENERIC_TENANT.branding);
            } else {
                throw new Error(`HTTP ${response.status}`);
            }

        } catch (err) {
            // Fallback: backend caído, timeout, o error de red.
            // Usamos los datos hardcodeados para que la app no quede colgada.
            console.warn('[TenantContext] Backend no disponible, usando fallback local:', err);
            const { value: subdomain } = getTenantFromHostname();
            const fallback = subdomain === 'itobox' ? ITOBOX_TENANT : GENERIC_TENANT;
            setTenant({ ...fallback, branding: { ...fallback.branding, ...getBrandingOverride() } });
            applyBranding(fallback.branding);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTenant();
    }, []);

    const isWhiteLabel = tenant?.plan?.id === 'enterprise' && tenant?.has_custom_domain;

    return (
        <TenantContext.Provider
            value={{
                tenant,
                loading,
                error,
                isWhiteLabel,
                refreshTenant: fetchTenant
            }}
        >
            {children}
        </TenantContext.Provider>
    );
};

export default TenantContext;
